/**
 * Wallet-address helpers — chain-aware validation + normalization.
 *
 * THE rule for the EVM→Solana migration: EVM addresses (`0x` + 40 hex) are
 * CASE-INSENSITIVE, so we lowercase them to get one canonical key. Solana
 * addresses are base58 and CASE-SENSITIVE — lowercasing a base58 address
 * SILENTLY CORRUPTS it into a different (but still valid-looking) address, which
 * then forks a user into phantom accounts across the JSONL stores. So
 * `normalizeWallet` lowercases ONLY strict `0x`-EVM addresses and passes
 * everything else through UNCHANGED. Use these everywhere instead of a raw
 * `.toLowerCase()` / inline `0x` regex / viem-or-ethers `isAddress` on a wallet.
 */

const EVM_RE = /^0x[a-fA-F0-9]{40}$/;
// Base58 alphabet (Bitcoin/Solana): 1-9, A-Z minus I/O, a-z minus l. A Solana
// public key is 32 bytes → 32-44 base58 chars. This is a FORMAT check, not a
// full decode; the real round-trip check happens when web3.js parses it.
const SOLANA_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/** True if `a` is a `0x` EVM address (case-insensitive hex). */
export function isEvmAddress(a: unknown): a is string {
  return typeof a === "string" && EVM_RE.test(a.trim());
}

/** True if `a` looks like a Solana base58 address (format check, not a decode). */
export function isSolanaAddress(a: unknown): a is string {
  return typeof a === "string" && SOLANA_RE.test(a.trim());
}

/** True if `a` is a valid wallet address on either chain. */
export function isValidWallet(a: unknown): a is string {
  return isEvmAddress(a) || isSolanaAddress(a);
}

/**
 * Canonical key for a wallet address. Lowercases ONLY EVM (`0x`) addresses;
 * returns Solana base58 (and anything else) untouched — lowercasing base58
 * corrupts it. Always trims surrounding whitespace. This is the drop-in
 * replacement for raw `.toLowerCase()` on a wallet.
 */
export function normalizeWallet(a: string): string {
  const t = a.trim();
  return EVM_RE.test(t) ? t.toLowerCase() : t;
}
